import { ErogantiDefRow } from "@bonusmap/api-types";
import { BaseWebComponent } from "../base-components/base-web-component";
import { convertArray } from "@bonusmap/shared_ui/library";
import { templateParse } from "../../logic/templateParse.js";
import { createChoicesInstance } from "../../utils/createChoicesInstance";

// import Choices from 'choices.js';
const { Choices } = window;

/**
 * Componente web per la gestione e selezione degli eroganti bonus.
 * Utilizza Choices.js per fornire un'interfaccia di ricerca e selezione avanzata.
 * Supporta sia modalità readonly che interattiva.
 * 
 * @extends BaseWebComponent
 */
export class BonusEroganti extends BaseWebComponent {
    /** Nome del web component per la registrazione nel DOM */
    static webComponentName = 'bonus-eroganti';
    
    /** Attributi osservati per il ciclo di vita del componente */
    static observedAttributes = ['readonly'];
    
    /** ID del template utilizzato per il rendering */
    protected _templateId: string = "bonus-eroganti";
    
    /** Valore iniziale del campo - stringa con ID degli eroganti separati da virgola */
    private initialValue: string = "";
    
    /** Dataset corrente contenente i risultati della ricerca degli eroganti */
    private _currentDataset: ErogantiDefRow[] = [];

    /**
     * Getter per il dataset corrente
     * @returns {ErogantiDefRow[]} Il dataset corrente degli eroganti
     */
    get currentDataset(): ErogantiDefRow[] {
        return this._currentDataset;
    }

    /**
     * Setter privato per il dataset corrente
     * @param {ErogantiDefRow[]} newDataset - Il nuovo dataset da impostare
     */
    private set currentDataset(newDataset: ErogantiDefRow[]) {
        this._currentDataset = newDataset;
    }
    
    /** Selezione corrente di eroganti */
    private _currentSelection: ErogantiDefRow[] = [];
    
    /**
     * Getter per la selezione corrente di eroganti
     * @returns {ErogantiDefRow[]} Le eroganti attualmente selezionate
     */
    get currentSelection(): ErogantiDefRow[] {
        return this._currentSelection;
    }
    
    /**
     * Setter privato per la selezione corrente
     * Sincronizza automaticamente il valore con la selezione
     * @param {ErogantiDefRow[]} newSelection - Le nuove eroganti selezionate
     */
    private set currentSelection(newSelection: ErogantiDefRow[]) {
        this._currentSelection = newSelection;
        // Sincronizza il valore con la selezione corrente
        const ids = newSelection.map(item => item.id.toString() as string) || [];
        this.value = ids;
    }
    
    /** Array di ID degli eroganti selezionate come stringhe */
    private _value: string[] = [];
    
    /**
     * Getter per il valore corrente (array di ID degli eroganti)
     * @returns {string[]} Gli ID degli eroganti selezionate
     */
    get value(): string[] {
        return this._value;
    }

    /**
     * Setter per il valore corrente
     * Accetta sia array di stringhe che di numeri, convertendo tutto in stringhe
     * @param {string[] | number[]} newValue - I nuovi ID degli eroganti
     */
    private set value(newValue: string[] | number[]) {
        if (Array.isArray(newValue)) {
            this._value = newValue.map(v => typeof v === "number" ? v.toString() : v);
            this.valueChanged(this._value);
        }
    }
    
    /**
     * Callback chiamato quando il valore cambia
     * Emette un evento personalizzato per notificare il cambiamento
     * @param {string[]} newValue - Il nuovo valore
     */
    protected valueChanged(newValue: string[]): void {
        // Dispatch event per notificare il cambio di valore
        this.dispatch('eroganti-value-changed', {
            value: newValue,
            selection: this.currentSelection
        });
    }
    
    /**
     * Getter per il valore iniziale convertito in array
     * @returns {string[]} Array di ID degli eroganti dal valore iniziale
     */
    private get initialValueArray(): string[] {
        return this.initialValue
            .split(',')
            .map(v => v.trim())
            .filter(v => v.length > 0);
    }
    /**
     * Costruttore del componente.
     * Inizializza il valore iniziale dall'attributo 'value' e imposta i listener globali.
     */
    constructor() {
        super();
        this.initialValue = this.attributeStringOption("value", "");
        this.setGlobalListeners();
    }
    
    /**
     * Determina se il componente è in modalità sola lettura
     * @returns {boolean} True se il componente è readonly o disabled
     */
    get readOnly(): boolean {
        return this.hasAttribute('readonly') || this.hasAttribute('disabled');
    }
    
    /**
     * Recupera il dataset iniziale degli eroganti basato sul valore iniziale
     * @returns {Promise<ErogantiDefRow[]>} Le eroganti corrispondenti agli ID iniziali
     */
    private async getInitialDataset(): Promise<ErogantiDefRow[]> {
        // Ottiene il dataset iniziale basato sul valore iniziale

        if (!this.initialValue) {
            return [];
        }
        const initialValueArray = this.initialValueArray;
        const initialValueArrayNumbers = convertArray<number>(initialValueArray, "number") as number[];
        this.value = initialValueArrayNumbers;
        if (initialValueArrayNumbers.length === 0) return [];
        try {
            const result = await window.api.manager.eroganti.getByIds(initialValueArrayNumbers);
            if (result.success && Array.isArray(result.data)) {
                return result.data as ErogantiDefRow[] || [];
            }
            else {
                console.error(`Error fetching initial eroganti data: ${result.error}`);
            }
        } catch (error) {
            console.error(`Error fetching initial  data: ${error}`);
        }
        return [];
    }
    
    /**
     * Elabora il valore iniziale del componente.
     * Carica i dati degli eroganti corrispondenti agli ID iniziali e li imposta in Choices.js
     * @returns {Promise<void>}
     */
    private async processInitialValue(): Promise<void> {
        if (this.initialValue && this.choicesInstance) {
            try {
                const resultData = await this.getInitialDataset();
                
                if (resultData && resultData.length > 0) {
                    // Crea gli oggetti choice per Choices.js
                    const choicesData = resultData.map(item => ({
                        value: item.id.toString(),
                        label: item.title || "",
                        customProperties: {
                            row: item
                        }
                    }))
                    // Aggiunge le scelte e le seleziona
                    this.choicesInstance.setChoices(choicesData, 'value', 'label', false);
                    // Seleziona i valori iniziali usando l'array initialValueArray originale
                    setTimeout(() => {
                        this.initialValueArray.forEach(id => {
                            this.choicesInstance.setChoiceByValue(id);
                        });
                        // Sincronizza la selezione dopo che tutti i valori sono stati selezionati
                        this.syncSelectionFromChoices();
                    }, 100);
                }
            } catch (error) {
                console.error(`Error fetching initial eroganti data: ${error}`);
            }
        }
    }
    
    /**
     * Sincronizza la selezione interna con i valori selezionati in Choices.js
     * Aggiorna sia il valore che la selezione corrente con i dati completi degli eroganti
     */
    private syncSelectionFromChoices(): void {
        const selectedValues = this.choicesInstance.getValue(true);
        this.value = Array.isArray(selectedValues) ? selectedValues : [selectedValues].filter(Boolean);

        // Aggiorna currentSelection con i dati completi degli eroganti
        const newSelection: ErogantiDefRow[] = [];
        const choices = this.choicesInstance.getValue();
        const choicesArray = Array.isArray(choices) ? choices : [choices];

        choicesArray.forEach((choice: any) => {
            if (choice.customProperties?.row) {
                newSelection.push(choice.customProperties.row);
            }
        });
        this._currentSelection = newSelection;
    }
    
    /**
     * Imposta i listener globali per il componente
     * Attualmente vuoto ma preparato per future estensioni
     */
    private setGlobalListeners(): void {

    }
    /**
     * Hook chiamato dopo il rendering del componente.
     * Inizializza gli elementi UI e configura Choices.js se non in modalità readonly.
     */
    protected afterRender(): void {
        if (this.readOnly) {
            return;
        }
        // Inizializza gli elementi UI dopo il rendering
        this.listSelect = this.roleElementSelector("eroganti-select") as HTMLSelectElement;

        // Inizializza Choice.js
        this.initializeChoices();

        // Elabora il valore iniziale se presente
        this.processInitialValue();

        this.valueChanged(this.value);
    }
    
    /**
     * Inizializza l'istanza di Choices.js con configurazione e listener.
     * Configura la ricerca live e gli eventi di selezione/rimozione.
     */
    private initializeChoices(): void {
        this.choicesInstance = createChoicesInstance(this.listSelect, {
            removeItemButton: true,
            searchEnabled: true,
            loadingText: 'Ricerca in corso...',
            noResultsText: 'Nessuna ente trovato',
            noChoicesText: 'Digita almeno un carattere per cercare',
            searchPlaceholderValue: 'Digita per cercare eroganti...',
            maxItemCount: 20,
            removeItemIconText: () => 'Rimuovi elemento',
            callbackOnCreateTemplates: (template) => {
                return {
                    item: (config: any, choice: any, removeItemButton: any) => {
                        // Chiamata al template originale
                        const originalItem = Choices.defaults.templates.item.call(
                            this.choicesInstance, config, choice, removeItemButton
                        );


                        return originalItem;
                    }
                };
            }
        });

        // Ascolta gli eventi di Choice.js  
        this.listSelect.addEventListener('change', () => {
            this.syncSelectionFromChoices();
        });

        // Ascolta gli eventi addItem e removeItem per una migliore sincronizzazione
        this.listSelect.addEventListener('addItem', () => {
            this.syncSelectionFromChoices();
        });

        this.listSelect.addEventListener('removeItem', () => {
            this.syncSelectionFromChoices();
        });

        // Ascolta l'input di ricerca per la ricerca live
        this.listSelect.addEventListener('search', (event: any) => {
            const searchTerm = event.detail.value;
            // if (searchTerm && searchTerm.length >= 1) {
            // consente l'avvio anche se il termine è vuoto
            this.performSearch(searchTerm);
            // }
        });

        // Carica i dati iniziali al focus se il dropdown è vuoto
        this.listSelect.addEventListener('showDropdown', (event: any) => {
            const currentSearchTerm = this.choicesInstance.input?.value || '';
            this.performSearch(currentSearchTerm);
        });
    }
    
    /**
     * Esegue una ricerca degli eroganti basata sul termine inserito.
     * Filtra i risultati escludendo le eroganti già selezionate.
     * @param {string} searchTerm - Il termine di ricerca inserito dall'utente
     * @returns {Promise<void>}
     */
    private async performSearch(searchTerm: string): Promise<void> {
        if (!searchTerm || searchTerm.length == 0) {
            this.choicesInstance.clearChoices();
            // return; <- commentata per permettere ricerca senza termine
        }

        try {

            let whereClause = searchTerm && searchTerm.length > 0
                ? `title ILIKE $1`
                : `1=1`; // Cerca tutti gli elementi se non c'è termine di ricerca
            let params = searchTerm && searchTerm.length > 0
                ? [`%${searchTerm}%`]
                : [];
            const result = await window.api.manager.eroganti.query(whereClause, params, 'title', 20)
            if (result.success && result.data && result.data.length > 0) {
                // Filtra gli elementi già selezionati
                const filteredData = result.data.filter(item =>
                    !this.value.includes(item.id.toString())
                );

                this._currentDataset = filteredData as ErogantiDefRow[];

                const choicesData = filteredData.map(item => ({
                    value: item.id.toString()||"",
                    label: item.title || "",
                    customProperties: {
                        row: item
                    }
                }));

                this.choicesInstance.setChoices(choicesData as any, 'value', 'label', true);
            } else {
                this.choicesInstance.clearChoices();
            }
        } catch (error) {
            console.error(`Error searching  data:`, error);
            this.choicesInstance.clearChoices();
        }
    }
    
    // --------------------
    // Elementi del componente
    // --------------------
    
    /** Istanza di Choices.js per la gestione dell'interfaccia di selezione */
    private choicesInstance!: InstanceType<Window['Choices']>; 
    
    /** Elemento select HTML utilizzato da Choices.js */
    private listSelect!: HTMLSelectElement; 

    /**
     * Semplifica il dataset per il rendering in modalità readonly
     * @param {ErogantiDefRow[]} dataset - Il dataset degli eroganti
     * @returns {any[]} Dataset semplificato contenente solo titolo e descrizione
     */
    private simplifyDataset(dataset: ErogantiDefRow[]): any[] {
        return dataset.map(item => ({
            title: item.title,
            description: item.description || null
        }));
    }

    /**
     * Metodo di rendering principale del componente.
     * Gestisce sia la modalità interattiva che quella readonly.
     * In modalità readonly, carica prima i dati iniziali e poi renderizza.
     */
    protected render(): void {
        if (this.readOnly) {
            this.renderingData = this.buildRenderingData();
            this.getInitialDataset()
                .then((data) => {
                    this.renderingData = {
                        ...this.renderingData,
                        readonly: true,
                        dataset: this.simplifyDataset(data),
                        datasetLength: data && Array.isArray(data) ? data.length : 0,
                    }
                    const html = templateParse(this.templateId, this.renderingData);

                    if (html) {
                        this.innerHTML = html;
                        this.afterRender();
                    } else {
                        console.error(`Error rendering template '${this.templateId}' for ${this.tagName}`);
                        this.onRenderError();
                    }
                })
                .catch(error => {
                    console.error(`Error during initial dataset fetch: ${error}`);
                });
        } else {
            super.render();
        }
    }
}